import { createClient } from 'matrix-js-sdk';
import { useMatrixStore } from '../state/matrixStore';


export class MatrixClientService {
  constructor() {
    this.client = null;
    this.isInitialized = false;
    this._timelineHandler = null;
    this._redactionHandler = null;

    if (typeof MatrixClientService._roomTimelineAttached === 'undefined') {
      MatrixClientService._roomTimelineAttached = false;
    }
  }

    /**
     * Initialize the Matrix client with user credentials
     * @param {string} userId - Matrix user ID (@user:domain)
     * @param {string} accessToken - Access token for the user
     * @param {string} baseUrl - Homeserver URL from /api/matrix/credentials (required)
     */
    async initialize(userId, accessToken, baseUrl) {
    if (!baseUrl) {
        throw new Error('Matrix initialize() missing baseUrl from /api/matrix/credentials.');
    }

    // Use in-memory store (default). IndexedDB cache caused STOPPED sync state
    // on page refresh, preventing messages from loading. Fresh initial sync
    // on every page load is fast enough with initialSyncLimit and guarantees
    // PREPARED fires so messages are always available.
    this.client = createClient({
        baseUrl,
        accessToken,
        userId,
    });

    this.isInitialized = true;
    return this.client;
    }




  /**
   * Start the Matrix client and wait until first sync finishes enough
   * for rooms/account-data to be available (PREPARED or SYNCING).
   */
  async startClient() {
    if (!this.client || !this.isInitialized) {
      throw new Error('Matrix client not initialized. Call initialize() first.');
    }

    // Start syncing
    await this.client.startClient({ initialSyncLimit: 10 });

    // Attach a Room.timeline listener once, to feed all m.room.message events into matrixStore
    if (!this._timelineHandler && !MatrixClientService._roomTimelineAttached) {
      MatrixClientService._roomTimelineAttached = true;

    // Add a cache to track recently processed events
    const recentlyProcessed = new Map();
    const CACHE_TTL = 5000; // 5 seconds


const handler = (event, room, toStartOfTimeline, removed, data) => {
  try {
    if (!room || !event || typeof event.getType !== 'function') return;
    const eventType = event.getType();

    // Handle m.reaction events (emoji reactions)
    if (eventType === 'm.reaction') {
      const content = typeof event.getContent === 'function' ? event.getContent() : {};
      const relatesTo = content?.['m.relates_to'];
      if (!relatesTo || relatesTo.rel_type !== 'm.annotation') return;

      const targetEventId = relatesTo.event_id;
      const emoji = relatesTo.key;
      if (!targetEventId || !emoji) return;

      const reactionEventId = typeof event.getId === 'function' ? event.getId() : null;
      const sender = typeof event.getSender === 'function' ? event.getSender() : null;
      if (!reactionEventId || !sender) return;

      const store = useMatrixStore.getState();
      store.addReaction(room.roomId, targetEventId, emoji, sender, reactionEventId);
      return;
    }

    if (eventType !== 'm.room.message') return;

    const content = (typeof event.getContent === 'function' ? event.getContent() : {}) || {};

    // Handle message edits (m.replace)
    const relatesTo = content?.['m.relates_to'];
    if (relatesTo && relatesTo.rel_type === 'm.replace') {
      const originalEventId = relatesTo.event_id;
      const newContent = content?.['m.new_content'];
      const newBody = newContent?.body || '';
      if (originalEventId && newBody) {
        const store = useMatrixStore.getState();
        store.updateMessageBody(room.roomId, originalEventId, newBody);
      }
      return;
    }

    const eventId = typeof event.getId === 'function' ? event.getId() : null;
    const roomId = room.roomId;

    // Create a unique key for this event
    const cacheKey = `${roomId}:${eventId}`;

    // Check if we recently processed this event
    if (recentlyProcessed.has(cacheKey)) return;

    // Mark as processed
    recentlyProcessed.set(cacheKey, Date.now());

    // Clean up old entries periodically
    if (recentlyProcessed.size > 100) {
      const now = Date.now();
      for (const [key, timestamp] of recentlyProcessed.entries()) {
        if (now - timestamp > CACHE_TTL) {
          recentlyProcessed.delete(key);
        }
      }
    }

    const store = useMatrixStore.getState();
    const body = content.body || '';
    const msgtype = content.msgtype || '';

    // Transaction id for local echo; several SDKs expose this differently, so we defensively check
    const unsigned = typeof event.getUnsigned === 'function' ? event.getUnsigned() || {} : {};
    const txnId =
      (typeof event.getTxnId === 'function' && event.getTxnId()) ||
      event._txnId ||
      unsigned.transaction_id ||
      null;

    const sender = typeof event.getSender === 'function' ? event.getSender() : null;
    const timestamp =
      typeof event.getTs === 'function' ? event.getTs() : Date.now();

// Extract reply-to info if this message is a reply.
// Try multiple sources because some SDK versions strip m.relates_to from getContent().
let replyToEventId = null;
try {
  // 1) getContent() — works for local echo and some SDK versions
  replyToEventId = content?.['m.relates_to']?.['m.in_reply_to']?.event_id || null;
  // 2) Raw wire content — works when SDK processes the content
  if (!replyToEventId && event.event?.content?.['m.relates_to']?.['m.in_reply_to']?.event_id) {
    replyToEventId = event.event.content['m.relates_to']['m.in_reply_to'].event_id;
  }
  // 3) getWireContent() — another SDK accessor for raw content
  if (!replyToEventId && typeof event.getWireContent === 'function') {
    const wire = event.getWireContent() || {};
    replyToEventId = wire?.['m.relates_to']?.['m.in_reply_to']?.event_id || null;
  }
} catch (e) { /* defensive */ }

// Strip Matrix reply fallback from body (lines starting with "> " up to first blank line).
// Always strip if body matches the fallback pattern, even if replyToEventId extraction failed.
let displayBody = body;
if (typeof body === 'string' && body.startsWith('> ')) {
  const idx = body.indexOf('\n\n');
  if (idx >= 0) {
    displayBody = body.slice(idx + 2);
    // If we couldn't get replyToEventId from content, try to detect it's a reply from the pattern
    if (!replyToEventId) {
      // Body matches reply fallback pattern — mark as reply even without event_id
      // (won't be able to show quoted original, but at least strips the fallback)
    }
  }
}

// 1) Add/update the message in the per-room message list (dedupe logic is inside matrixStore)
store.addMatrixEvent(room.roomId, {
  event,
  eventId,
  txnId,
  sender,
  body: displayBody,
  rawBody: body,
  msgtype,
  timestamp,
  replyToEventId,
});

// NEW: unread tracking (WhatsApp-style)
try {
  const myMxid = typeof this.client?.getUserId === 'function' ? this.client.getUserId() : null;

  // Only treat as "new/unread" when the SDK says this event is LIVE (not backfill / initial sync replay)
  const isLiveEvent = !!(data && data.liveEvent);

  // Only count unread for messages NOT sent by me
  const isMine = !!(myMxid && sender && String(sender) === String(myMxid));

  const activeRoomId = useMatrixStore.getState().selectedRoomId;
  const isActiveRoom = !!(activeRoomId && String(activeRoomId) === String(roomId));

  const isTextMessage = String(msgtype) === 'm.text';

  // Only increment/clear unread for LIVE events (not during initial sync/backfill)
  if (isLiveEvent && isTextMessage && !isMine) {
    if (isActiveRoom) {
      store.clearUnreadCount(roomId);
      // Persist the read state so it survives page refresh
      if (myMxid) {
        store.markRoomRead(myMxid, roomId, timestamp);
      }
    } else {
      // Check durable read state: don't count events older than last read
      const lastReadTs = (store.lastReadTsByRoom || {})[roomId] || 0;
      if (!lastReadTs || timestamp > lastReadTs) {
        store.incrementUnreadCount(roomId);
      }
    }
  }

} catch (e) {
  console.warn('[MatrixClientService] unread tracking failed', e);
}


// 2) Update room "last activity" so getSortedRooms() moves it to the top
//    (matrixStore.getSortedRooms sorts by lastMessageTs) :contentReference[oaicite:2]{index=2}
store.upsertRoom({
  roomId,
  lastMessageTs: timestamp,
  lastMessage: displayBody,
});

// 3) Unread increment is handled above in the "unread tracking (WhatsApp-style)" block.
//    Do not increment here or it will double-count and also count my own sent messages.

  } catch (err) {
    console.error('[MatrixClientService] Room.timeline handler error', err);
  }
};

      this.client.on('Room.timeline', handler);
      this._timelineHandler = handler;
    }

    // Attach Room.receipt listener for cross-device read receipts
    if (!this._receiptHandler) {
      const receiptHandler = (event, room) => {
        try {
          if (!room || !event) return;
          const myMxid = typeof this.client?.getUserId === 'function' ? this.client.getUserId() : null;
          if (!myMxid) return;

          const content = typeof event.getContent === 'function' ? event.getContent() : {};
          // content shape: { eventId: { "m.read": { userId: { ts } } } }
          const store = useMatrixStore.getState();
          const roomId = room.roomId;

          for (const [eventId, receiptTypes] of Object.entries(content)) {
            const readers = receiptTypes?.['m.read'] || receiptTypes?.['m.read.private'] || {};

            // Track MY receipts (cross-device sync)
            if (readers[myMxid]) {
              const ts = readers[myMxid].ts;
              if (ts) {
                const currentReadTs = (store.lastReadTsByRoom || {})[roomId] || 0;
                if (ts > currentReadTs) {
                  store.markRoomRead(myMxid, roomId, ts);
                  store.clearUnreadCount(roomId);
                }
              }
            }

            // Track OTHER users' receipts (for "Seen" indicators)
            for (const [userId, receiptData] of Object.entries(readers)) {
              if (userId !== myMxid && receiptData?.ts) {
                store.setRemoteReadReceipt(roomId, userId, eventId, receiptData.ts);
              }
            }
          }
        } catch (err) {
          console.warn('[MatrixClientService] Room.receipt handler error', err);
        }
      };

      this.client.on('Room.receipt', receiptHandler);
      this._receiptHandler = receiptHandler;
    }

    // Attach Room.redaction listener to remove redacted reactions from the store
    if (!this._redactionHandler) {
      const redactionHandler = (event, room) => {
        try {
          if (!room || !event) return;
          // event.event.redacts holds the ID of the message that was deleted
          const redactedEventId = event?.event?.redacts
            || (typeof event.getAssociatedId === 'function' ? event.getAssociatedId() : null)
            || (typeof event.getId === 'function' ? event.getId() : null);
          if (!redactedEventId) return;

          const store = useMatrixStore.getState();
          store.removeReactionByEventId(room.roomId, redactedEventId);
          store.redactMessage(room.roomId, redactedEventId);
        } catch (err) {
          console.warn('[MatrixClientService] Room.redaction handler error', err);
        }
      };

      this.client.on('Room.redaction', redactionHandler);
      this._redactionHandler = redactionHandler;
    }

    // Wait for initial sync so m.direct and rooms exist before UI queries
    await new Promise((resolve) => {
      const onSync = (state/*, prevState, res */) => {
        // PREPARED happens after initial sync; SYNCING is also fine for room list
        if (state === 'PREPARED' || state === 'SYNCING') {
          this.client.removeListener('sync', onSync);
          resolve();
        }
      };
      this.client.on('sync', onSync);
    });

    return this.client;
  }


  /**
   * Send a read receipt for the last event in a room (best-effort).
   * Marks the room as fully read on the Matrix server so other devices
   * and the homeserver's unread counter stay in sync.
   */
  async sendReadReceipt(roomId) {
    if (!this.client || !roomId) return;
    try {
      const room = this.client.getRoom(roomId);
      if (!room) return;
      const events = room.getLiveTimeline().getEvents();
      if (!events.length) return;
      const lastEvent = events[events.length - 1];
      const lastEventId = typeof lastEvent.getId === 'function' ? lastEvent.getId() : null;
      if (!lastEventId) return;

      await this.client.setRoomReadMarkers(roomId, lastEventId);
    } catch (err) {
      // Best-effort: don't crash if the homeserver rejects the receipt
      console.warn('[MatrixClientService] sendReadReceipt failed for', roomId, err?.message || err);
    }
  }

  /**
   * Stop the Matrix client
   */
    async stopClient() {
    if (this.client) {
        // Explicitly remove listeners before stopping to prevent leaks
        if (this._timelineHandler) {
          try { this.client.removeListener('Room.timeline', this._timelineHandler); } catch {}
        }
        if (this._receiptHandler) {
          try { this.client.removeListener('Room.receipt', this._receiptHandler); } catch {}
        }
        if (this._redactionHandler) {
          try { this.client.removeListener('Room.redaction', this._redactionHandler); } catch {}
        }
        try { await this.client.stopClient(); } catch {}
    }
    this._timelineHandler = null;
    this._receiptHandler = null;
    this._redactionHandler = null;
    MatrixClientService._roomTimelineAttached = false;
    }


  /**
   * Create a 1:1 DM room and send initial message
   * @param {string} inviteeUserId - Matrix ID of user to invite
   * @param {string} initialMessage - Initial message to send
   */
  async createDirectMessage(inviteeUserId, initialMessage = '') {
    if (!this.client || !this.isInitialized) {
      throw new Error('Matrix client not initialized');
    }

    try {
      // Create the room
      const roomId = await this.client.createRoom({
        visibility: 'private',
        preset: 'trusted_private_chat',
        is_direct: true,
        invite: [inviteeUserId],
      });


      // Send initial message if provided
      if (initialMessage.trim()) {
        await this.client.sendTextMessage(roomId, initialMessage);
      }

      return {
        success: true,
        roomId: roomId
      };

    } catch (error) {
      console.error('Failed to create DM room:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Send a message in a room
   */
  async sendMessage(roomId, message) {
    if (!this.client || !this.isInitialized) {
      throw new Error('Matrix client not initialized');
    }

    try {
      await this.client.sendTextMessage(roomId, message);
      return { success: true };
    } catch (error) {
      console.error('Failed to send message:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Send a reply to a specific message
   */
  async sendReply(roomId, replyToEventId, replyToSender, replyToBody, message) {
    if (!this.client || !this.isInitialized) {
      throw new Error('Matrix client not initialized');
    }

    // Build Matrix reply fallback body
    const fallbackBody = `> <${replyToSender || ''}> ${(replyToBody || '').split('\n').join('\n> ')}\n\n${message}`;

    return this.client.sendEvent(roomId, 'm.room.message', {
      msgtype: 'm.text',
      body: fallbackBody,
      'm.relates_to': {
        'm.in_reply_to': {
          event_id: replyToEventId,
        },
      },
    });
  }

  /**
   * Send an emoji reaction to a message
   */
  async sendReaction(roomId, targetEventId, emoji) {
    if (!this.client || !this.isInitialized) {
      throw new Error('Matrix client not initialized');
    }

    return this.client.sendEvent(roomId, 'm.reaction', {
      'm.relates_to': {
        rel_type: 'm.annotation',
        event_id: targetEventId,
        key: emoji
      }
    });
  }

  /**
   * Remove a reaction by redacting its event
   */
  async removeReaction(roomId, reactionEventId) {
    if (!this.client || !this.isInitialized) {
      throw new Error('Matrix client not initialized');
    }

    return this.client.redactEvent(roomId, reactionEventId);
  }

  /**
   * Edit a previously sent message (Matrix m.replace)
   */
  async editMessage(roomId, originalEventId, newBody) {
    if (!this.client || !this.isInitialized) {
      throw new Error('Matrix client not initialized');
    }

    return this.client.sendEvent(roomId, 'm.room.message', {
      msgtype: 'm.text',
      body: `* ${newBody}`,
      'm.new_content': {
        msgtype: 'm.text',
        body: newBody,
      },
      'm.relates_to': {
        rel_type: 'm.replace',
        event_id: originalEventId,
      },
    });
  }

  /**
   * Delete a message by redacting its event
   */
  async deleteMessage(roomId, eventId) {
    if (!this.client || !this.isInitialized) {
      throw new Error('Matrix client not initialized');
    }

    return this.client.redactEvent(roomId, eventId);
  }

  /**
   * Get all direct message rooms
   */
    getDirectRooms() {
    if (!this.client) return [];

    // m.direct is a map: { "<userId>": ["!roomId1:domain", "!roomId2:domain", ...], ... }
    const dmContent = this.client.getAccountData('m.direct')?.getContent() || {};
    const dmRoomIds = new Set(
        Object.values(dmContent).flat().filter(Boolean)
    );

    // Keep only rooms whose roomId is listed in m.direct
    return this.client.getRooms().filter(r => dmRoomIds.has(r.roomId));
    }

  /**
   * Subscribe to room list changes that affect DMs.
   * Returns an unsubscribe function.
   */
  onRoomListChanged(callback) {
    if (!this.client) return () => {};

    const fire = () => {
      try {
        callback(this.getDirectRooms());
      } catch (_) {
        // no-op: keep UI resilient if called during early startup
      }
    };

    // Rooms created/joined/left
    const onRoom = () => fire();
    this.client.on('Room', onRoom);

    // New timeline events (can promote a room to visible)
    const onTimeline = () => fire();
    this.client.on('Room.timeline', onTimeline);

    // m.direct account-data changed
    const onAccountData = (ev) => {
      if (ev && ev.getType && ev.getType() === 'm.direct') fire();
    };
    this.client.on('accountData', onAccountData);

    // Fire once now (after startClient awaited)
    fire();

    // Unsubscribe
    return () => {
      this.client.removeListener('Room', onRoom);
      this.client.removeListener('Room.timeline', onTimeline);
      this.client.removeListener('accountData', onAccountData);
    };
  }

  /**
   * Join a room (accept invitation)
   */
  async joinRoom(roomIdOrAlias) {
    if (!this.client || !this.isInitialized) {
      throw new Error('Matrix client not initialized');
    }

    try {
      await this.client.joinRoom(roomIdOrAlias);
      return { success: true };
    } catch (error) {
      console.error('Failed to join room:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get room by ID
   */
  getRoom(roomId) {
    if (!this.client || !this.isInitialized) return null;
    return this.client.getRoom(roomId);
  }
}